import { compileStyles, createMediaStyles } from 'lib/styled-helpers'
import React, { ButtonHTMLAttributes, forwardRef } from 'react'
import styled from 'styled-components'

import { useAppConfig } from '../../application/configuration'
import { getProjectName } from '../../lib/get-project-name'
import { Icon } from '../icon'
import { EcomIconsId } from '../icon/types'

export interface Props extends ButtonHTMLAttributes<HTMLButtonElement> {
  size?: 'small' | 'medium' | 'big'
  block?: boolean
  link?: string
  secondary?: boolean
  disabled?: boolean
  projectName?: string
  icon?: EcomIconsId
  customSize?: string | null
  textSize?: string | null
  backgroundColor?: string | null
  background?: string
  borderRadius?: string
  targetBlank?: string
  textButton?: boolean
  text?: boolean
  uppercase?: boolean
}

export const Button = forwardRef<HTMLButtonElement, Props>(
  (
    {
      size = 'small',
      block = false,
      link,
      children,
      secondary = false,
      disabled = false,
      icon,
      customSize,
      textSize,
      background,
      borderRadius,
      targetBlank,
      textButton,
      text,
      uppercase,
      ...restProps
    },
    ref
  ) => {
    let { projectName } = useAppConfig()

    if (link && getProjectName() !== 'avito') {
      return (
        <StyledButton
          as="a"
          projectName={projectName}
          background={background}
          href={link}
          size={size}
          customSize={customSize}
          textSize={textSize}
          block={block}
          secondary={secondary}
          disabled={disabled}
          borderRadius={borderRadius}
          target={targetBlank}
          textButton={textButton}
        >
          {children}
        </StyledButton>
      )
    }

    return (
      <StyledButton
        size={size}
        customSize={customSize}
        background={background}
        block={block}
        ref={ref}
        disabled={disabled}
        {...restProps}
        secondary={secondary}
        projectName={projectName}
        borderRadius={borderRadius}
        textButton={textButton}
        text={text}
        uppercase={uppercase}
        textSize={textSize}
      >
        {children}
        {getProjectName() === 'avito' && icon && (
          <IconWrap>
            <Icon name={icon} size={0.8} />
          </IconWrap>
        )}
      </StyledButton>
    )
  }
)

const StyledButton = styled.button<Props>`
  display: ${(props) => (props.block ? 'flex' : 'inline-flex')};
  display: ${() => getProjectName() === 'avito' && 'flex'};
  white-space: nowrap;
  justify-content: center;
  align-items: center;
  width: ${({ block }) => (block ? '100%' : 'auto')};
  width: ${({ block }) => getProjectName() === 'avito' && block && '100%'};
  ${({ disabled }) => disabled && 'opacity: 0.2; pointer-events: none;'};
  height: ${({ size, theme, customSize }) => {
    if (customSize) return customSize

    if (size === 'small') {
      return theme.spacing[9]
    } else if (size === 'medium') {
      return theme.spacing[14]
    } else {
      return theme.spacing[16]
    }
  }};
  padding: ${({ theme }) => `${theme.spacing[0]} ${theme.spacing[6]}`};
  border: none;
  border-radius: ${() => (getProjectName() === 'avito' ? '16px' : 0)};
  border-radius: ${({ borderRadius }) => borderRadius};
  background-color: ${({ theme, secondary, backgroundColor, text }) => {
    if (text) return 'transparent'
    return backgroundColor ? backgroundColor : secondary ? theme.palette.brand.primaryDark : theme.palette.brand.primary
  }};
  background: ${({ background }) => getProjectName() === 'avito' && background};
  background: ${({ theme }) => getProjectName() === 'mercedes-benz' && theme.palette.grey[900]};
  text-align: center;
  color: ${({ theme, secondary }) => (secondary && getProjectName() === 'avito' ? theme.palette.black : '#fff')};
  color: ${({ theme, text }) => (text ? theme.palette.black : '#fff')};
  cursor: pointer;
  outline: none;
  transition: background-color 0.25s;
  text-transform: ${({ uppercase }) => uppercase && 'uppercase'};
  text-decoration: none;

  &:hover {
    background-color: ${({ theme, secondary }) => {
      if (getProjectName() === 'infiniti') {
        return theme.palette.white
      }
      if (getProjectName() === 'volkswagen') {
        return theme.palette.brand.primaryDark
      }
      return secondary ? theme.palette.brand.secondaryDark : theme.palette.brand.secondary
    }};
    color: ${({ theme }) => {
      if (
        getProjectName() === 'volkswagen-dealer' ||
        getProjectName() === 'volkswagen' ||
        getProjectName() === 'mercedes-benz'
      ) {
        return theme.palette.black
      }
      return getProjectName() === 'infiniti' ||
        getProjectName() === 'basic-black' ||
        getProjectName() === 'haval' ||
        getProjectName() === 'vtb-leasing'
        ? theme.palette.brand.primary
        : '#fff'
    }};
    border: ${({ theme }) => {
      if (getProjectName() === 'mercedes-benz') return `2px solid` + theme.palette.grey[900]
      return getProjectName() === 'infiniti' || getProjectName() === 'basic-black'
        ? `1px solid` + theme.palette.brand.primary
        : null
    }};
  }

  ${({ theme }) => compileStyles(theme.textStyles.btn)};
  font-size: ${({ textSize }) => {
    if (getProjectName() === 'avito' && textSize) return textSize
    return '1.6rem'
  }};

  ${({ textButton }) => {
    return (
      textButton &&
      `
        background: transparent; 
        color: #000000;
        text-decoration: underline;
        display: inline;
        height: auto;
        font-size: 1.4rem !important;
        &:hover {
            background: none;
            color: #000000;
            text-decoration: none;
          }
        `
    )
  }};

  ${({ disabled, theme }) => {
    return (
      disabled &&
      `
      cursor: default;
      background: ${theme.palette.grey[500]};
      &:hover {
        background: ${theme.palette.grey[500]};
      }
    `
    )
  }}

  ${createMediaStyles(({ textSize, theme, size, customSize }) => ({
    phone: textSize && getProjectName() === 'bmw' && 'font-size: 9px !important; padding: 0 7px; height: 21px;',
    desktop:
      textSize &&
      getProjectName() === 'bmw' &&
      `font-size: 1.6rem !important; padding: ${theme.spacing[0]} ${theme.spacing[6]}; height: 32px;`
  }))}
`

const IconWrap = styled.div`
  margin-left: 7px;
  margin-top: 8px;
  ${createMediaStyles(() => ({
    phone: 'display: none;',
    tablet: 'display: block;'
  }))}
`
